// BkosFoundingCohortPage — registration landing for The BKOS Founding Cohort.
//
// Copy is synced from the CC asset (program bkos-founding-cohort, module FC01,
// asset ast-86a97220-07c7 / BKOS-Founding-Cohort-Landing-Page v2.2) rendered as
// React with the live form wired to POST /api/programs/bkos-founding-cohort/register.
// No automated CC→EE sync exists for this content — re-pull the asset via the
// Command Centre MCP get_asset_content tool and diff by hand whenever CC's copy
// changes again. TSB Global chrome
// (TakerShell nav + footer) wraps the page — Michael's direction 2026-06-12:
// TSB is driving BKOS adoption, so the TSB header/footer frame the page while
// the body keeps the BKOS brand greens.
//
// The endpoint answers { result: 'registered' | 'waitlisted', already } and
// the card swaps to the matching state: confirmation (email on its way) or
// waitlist — same promise as the waitlist email. Cap, kickoff date/time, and
// cadence live in D1 (cohort row + program email templates), never hardcoded
// here — the hero kickoff line (date AND time range) is resolved server-side
// from GET /api/programs/:slug/cohort-stats { kickoffDateLongYear,
// kickoffTimeRange }, both pulled from the cohort's first scheduled session via
// the SAME resolver (formatSessionWhen) that produces the confirmation-email
// {{kickoffDateLong}} / {{kickoffTimeRange}} tokens. Renders for either region
// once that cohort has a scheduled session; hidden otherwise.
//
// Cohort-number copy is region-aware (Lisa, 2026-07-06): the default NA page
// registers into Cohort 2, so it says "second cohort" / "next cohort"; the AU
// page is still its first cohort and keeps the founding-group copy.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import { MarketingOptIn } from './components/MarketingOptIn';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { getAttributionPayload } from './attribution';

const API = import.meta.env.VITE_API_BASE_URL as string;
const SLUG = 'bkos-founding-cohort';

type Result = 'registered' | 'waitlisted' | null;

export default function BkosFoundingCohortPage({ region }: { region?: 'au' } = {}) {
  const isAU = region === 'au';
  const navigate = useNavigate();
  // Brand-aware GTM — this page fetches no brand payload; it serves on the
  // TSB taker host, so the host fallback resolves the right container.
  useBrandGtm();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [step, setStep] = useState<'email' | 'details'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Result>(null);
  const [already, setAlready] = useState(false);
  // Live capacity — "N founding spots left" social-proof/scarcity on the landing.
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null);
  const [isFull, setIsFull] = useState(false);
  // Live kickoff When (date + time range) — resolved server-side from the cohort's
  // first scheduled session through the SAME resolver as the confirmation-email
  // {{kickoffDateLong}} / {{kickoffTimeRange}} tokens, so this never needs a
  // hand-edit on reschedule and works for either region. e.g.
  // "Thursday, July 23, 2026 · 1:00–3:00 PM EDT".
  const [kickoffWhen, setKickoffWhen] = useState<string | null>(null);
  // Multi-region routing (migration 0181): the CURRENT AU cohort id, resolved
  // server-side via GET /api/programs/:slug/cohort-stats?region=au (which
  // reads programs.active_au_cohort_id) — never a hardcoded literal, so a
  // future AU3 cutover needs zero taker deploy. Registration is disabled on
  // the AU page until this resolves, so an AU visitor can never silently
  // register into the NA cohort while this is still loading/failed.
  const [auCohortId, setAuCohortId] = useState<string | null>(null);

  // Auto-route Australian visitors who land on the default (NA) page to the AU
  // cohort, so they don't end up on the full North-America waitlist. Detection is
  // IP-based (GET /api/public/geo, ISO-2). Only redirects from the default page —
  // the /au page never redirects (no loop), and we skip once the visitor has
  // started filling the form so we never yank the page out from under them.
  useEffect(() => {
    if (isAU || email) return;
    let cancelled = false;
    fetch(`${API}/api/public/geo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (String(d.country || '').toUpperCase() === 'AU') {
          navigate('/programs/bkos-founding-cohort/au', { replace: true });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAU]);

  useEffect(() => {
    const qs = isAU ? `?region=au` : '';
    // fetchWithRetry matters here specifically: on the AU page, auCohortId
    // gates the registration button (auNotReady guard below) with no visible
    // error state — a single-shot fetch that blips leaves the button stuck
    // disabled with nothing telling the visitor why. Retrying absorbs that
    // before it ever reaches the visitor.
    fetchWithRetry(`${API}/api/programs/${SLUG}/cohort-stats${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        if (isAU && typeof d.cohortId === 'string' && d.cohortId) setAuCohortId(d.cohortId);
        setSpotsLeft(typeof d.spotsLeft === 'number' ? d.spotsLeft : null);
        setIsFull(!!d.isFull);
        // Prefer the year-inclusive date for the landing; append the time range
        // when present. Both come from formatSessionWhen, so a reschedule flows
        // through automatically — never a hardcoded literal, never a raw token.
        const dateStr = typeof d.kickoffDateLongYear === 'string' && d.kickoffDateLongYear
          ? d.kickoffDateLongYear
          : (typeof d.kickoffDateLong === 'string' && d.kickoffDateLong ? d.kickoffDateLong : null);
        const timeStr = typeof d.kickoffTimeRange === 'string' && d.kickoffTimeRange ? d.kickoffTimeRange : null;
        setKickoffWhen(dateStr ? (timeStr ? `${dateStr} · ${timeStr}` : dateStr) : null);
      })
      .catch(() => {});
  }, [isAU]);

  document.title = isAU
    ? 'The BKOS Founding Cohort (Australia) — Registration'
    : 'The BKOS Founding Cohort — Registration';

  // AU registration must never fall through to the default (NA) cohort — if
  // the AU cohort id hasn't resolved yet, refuse to submit rather than omit
  // cohortId (which would silently register the visitor into NA).
  const auNotReady = isAU && !auCohortId;

  async function register(body: Record<string, unknown>): Promise<Record<string, unknown> | null> {
    if (auNotReady) {
      setError('Still loading — give it a second and try again.');
      return null;
    }
    const res = await fetch(`${API}/api/programs/${SLUG}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: isAU ? 'landing-au' : 'landing',
        ...(isAU ? { cohortId: auCohortId } : {}),
        attribution: getAttributionPayload(),
        ...body,
      }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(
        d?.reason
          ? 'That email domain doesn’t look deliverable — double-check the address?'
          : d?.error ?? 'Something went wrong — please try again.',
      );
      return null;
    }
    return d;
  }

  // Step 1 — email only. The place is saved the moment it lands.
  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const d = await register({ email: email.trim(), stage: 'email' });
      if (!d) return;
      if (d.already) { // already fully registered before — skip the details step
        setResult(d.result === 'waitlisted' ? 'waitlisted' : 'registered');
        setAlready(true);
        document.getElementById('register')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      setStep('details');
    } catch {
      setError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Step 2 — names + country. Sends the (now personal) confirmation.
  async function submitDetails(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const d = await register({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        ...(country ? { country } : {}),
        stage: 'complete',
      });
      if (!d) return;
      setResult(d.result === 'waitlisted' ? 'waitlisted' : 'registered');
      setAlready(!!d.already);
      document.getElementById('register')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      setError('Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <TakerShell ctaHref="#register" ctaLabel="Count Me In" footerLabel="The BKOS Founding Cohort">
    <div className="bkfc">
      <style>{css}</style>

      <div className="bkfc-hero">
        <div className="bkfc-wrap">
          <div className="bkfc-brandline">BKOS</div>
          <div className="bkfc-eyebrow">
            {isAU
              ? 'The Founding Cohort · Australia · Registration Open'
              : 'The Founding Cohort · Registration Open'}
          </div>
          <h1>Be One of the First to<br />Move Your Practice Onto BKOS</h1>
          <p className="bkfc-sub">
            We're inviting a small group to implement BKOS together — with the team beside you
            every step. Not a course. Not a demo. A working group that gets you fully moved in.
          </p>
          <a className="bkfc-cta" href="#register">Count Me In</a>
          {kickoffWhen && (
            <div className="bkfc-kickoff">
              Kickoff: {kickoffWhen} · Included in your current licence, no additional cost
            </div>
          )}
          {isAU && kickoffWhen && (
            <div className="bkfc-kickoff-alt">
              That's 6:30am in Adelaide, 5:00am in Perth — sessions run mornings your time.
            </div>
          )}
        </div>
      </div>

      <section style={{ paddingTop: 0 }}>
        <div className="bkfc-wrap">
          <div className="bkfc-why">
            <h2>Why we built BKOS</h2>
            <p className="bkfc-body">
              We've made the decision to retire Pure Workflow. Every licensee is moving across to
              the Bookkeeper Operating System (BKOS) — our own platform, built specifically for
              bookkeeping practices, with the Pure Bookkeeping System woven directly into it.
            </p>
            <p className="bkfc-body">
              It's included in your current licence at no additional cost. Same investment, a
              platform we now build and control ourselves.
            </p>
            <div className="bkfc-pill-row">
              <div className="bkfc-pill">No additional cost</div>
              <div className="bkfc-pill">Direct implementation support</div>
              <div className="bkfc-pill">Your PB System, built in</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="bkfc-wrap">
          <h2>What the Founding Cohort is</h2>
          <p className="bkfc-body">
            BKOS has the Pure Bookkeeping System built in, integration with QBO and Xero, and AI
            functionality we couldn't have built into Pure Workflow — just some of what's in it.
            The Founding Cohort is how you make the move: together, hands-on, with direct support.
          </p>
          <div className="bkfc-story">
            <p><b>Founding members don't just use the platform first — they shape it.</b></p>
            <p>
              You'll be working directly with the team building BKOS. What you need, what slows
              you down, what would make your week easier — it goes straight onto the roadmap.
              That's what founding means here.
            </p>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="bkfc-wrap">
          <h2>What we'll do together</h2>
          <ul className="bkfc-agenda">
            {[
              ['Get you set up, properly.', 'Guided setup of your workspace, so day one feels like solid ground — not a blank screen.'],
              ['Put your first workflow live.', 'A Pure Bookkeeping System workflow, running on a real client — not a sandbox.'],
              ['Work together, weekly.', 'Live working sessions where we implement side by side and solve the real snags as they come up.'],
              ['Move your whole practice in.', 'By the end, BKOS is where your work lives.'],
              ['A direct line to the team.', 'Your questions don’t go into a queue. They go to the people building the platform.'],
            ].map(([title, body], i) => (
              <li key={title}>
                <div className="bkfc-tick">{i + 1}</div>
                <div><b>{title}</b> {body}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="bkfc-wrap">
          <div className="bkfc-founding">
            {isAU ? (
              <>
                <h3>Why we're opening this group up</h3>
                <p>
                  With everything we learned from the founding Australian group, we're able to
                  welcome a bigger group this round — while still keeping eyes on every setup,
                  every first workflow, every question.
                </p>
                <p>
                  Everything we learned from that first founding group is already baked into how
                  this one runs. If we do reach capacity, you'll be first in line for the next
                  intake.
                </p>
              </>
            ) : (
              <>
                <h3>Why we're opening this group up</h3>
                <p>
                  With everything we learned from the first couple of cohorts, we're able to
                  welcome a bigger group this round — while still keeping eyes on every setup,
                  every first workflow, every question.
                </p>
                <p>
                  Everything we learned from those first founding groups is already baked into how
                  this one runs. If we do reach capacity, you'll be first in line for the next
                  intake.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="bkfc-wrap">
          <h2>What founding members are telling us</h2>
          <div className="bkfc-story">
            <p>
              A few things keep coming up on our weekly calls with the founding cohort. People are
              genuinely excited that clients can finally log in and see their own portal —
              something Pure Workflow never let us offer. Several have told us the subdomain and
              setup process was more straightforward than they expected. And more than one person
              has said, unprompted, that watching this build in real time — features shipping and
              improving week to week — is the most fun they've had adopting a new tool.
            </p>
            <p>That's the kind of feedback that tells us we made the right call.</p>
          </div>
        </div>
      </section>

      <section style={{ paddingTop: 0 }}>
        <div className="bkfc-wrap">
          <div className="bkfc-lisa">
            <p>
              "I'll be working directly with this cohort through implementation — you won't be doing
              this alone. If you've got questions before you register, just reply to whichever email
              brought you here. I read every one."
            </p>
            <div className="bkfc-lisa-sig">— Lisa Campbell, CEO, Pure Bookkeeping</div>
          </div>
        </div>
      </section>

      <section id="register" style={{ paddingTop: 8 }}>
        <div className="bkfc-wrap">
          <div className="bkfc-regcard">
            {result === 'registered' && (
              <>
                <h2>You're in.</h2>
                <p>
                  {already
                    ? 'You were already registered — your place in the Founding Cohort is safe.'
                    : 'Welcome to the Founding Cohort. One quick thing before we start — two short questions so we can prep around where you are right now. Takes about a minute.'}
                </p>
                {!already && (
                  <a
                    className="bkfc-cta"
                    style={{ marginTop: 4 }}
                    href={`/take/bkos-welcome-survey?em=${encodeURIComponent(email.trim())}&fn=${encodeURIComponent(firstName.trim())}`}
                  >
                    Answer 2 quick questions →
                  </a>
                )}
                {/^\S+@\S+\.\S+$/.test(email) && (
                  <div style={{ marginTop: 20 }}>
                    <SmsOptIn
                      email={email}
                      firstName={firstName || null}
                      source="program-register"
                      sourceDetail="bkos-founding-cohort"
                      heading="Want session reminders by text?"
                    />
                    <MarketingOptIn
                      email={email}
                      firstName={firstName || null}
                      lastName={lastName || null}
                      source="program-register"
                      sourceDetail="bkos-founding-cohort"
                      programSlug="bkos-founding-cohort"
                    />
                  </div>
                )}
              </>
            )}
            {result === 'waitlisted' && (
              <>
                <h2>You're first in line for the next cohort.</h2>
                <p>
                  {isAU
                    ? "This cohort is full — we capped it on purpose, so we can keep eyes on everyone. You're on the named list for the next Australian intake, in the order you registered, and we'll reach out personally as soon as it has a start date."
                    : "This cohort is full — we capped it on purpose, so we can keep eyes on everyone. You're on the named list for the next cohort, in the order you registered, and we'll reach out personally as soon as it has a start date."}
                  {already ? ' (You were already on the list — your spot is safe.)' : ''}
                </p>
                {/^\S+@\S+\.\S+$/.test(email) && (
                  <div style={{ marginTop: 20 }}>
                    <SmsOptIn
                      email={email}
                      firstName={firstName || null}
                      source="waitlist"
                      sourceDetail="bkos-founding-cohort"
                      heading="Want a text when a spot opens?"
                    />
                    <MarketingOptIn
                      email={email}
                      firstName={firstName || null}
                      lastName={lastName || null}
                      source="waitlist"
                      sourceDetail="bkos-founding-cohort"
                      programSlug="bkos-founding-cohort"
                    />
                  </div>
                )}
              </>
            )}
            {result === null && (
              <>
                {isFull ? (
                  <div className="bkfc-spots bkfc-spots-full">
                    {isAU
                      ? "The founding group is full — add your name and you'll be first in line for the next intake."
                      : "This cohort is full — add your name and you'll be first in line for the next intake."}
                  </div>
                ) : spotsLeft != null && spotsLeft > 0 ? (
                  <div className="bkfc-spots">
                    Spots are limited — once we're full, you're first in line for the next intake.
                  </div>
                ) : null}
                <h2>Save your place</h2>
                <p>Tell us you're in. Kickoff details arrive with your confirmation email.</p>
                {step === 'email' ? (
                  <form onSubmit={submitEmail}>
                    <input
                      className="bkfc-field"
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                    <button className="bkfc-cta bkfc-submit" type="submit" disabled={busy || auNotReady}>
                      {busy ? 'Saving your place…' : 'Join the Founding Cohort'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={submitDetails}>
                    <div className="bkfc-regnote" style={{ marginTop: 0 }}>
                      Your place is saved. Just a couple of details to finish.
                    </div>
                    <input
                      className="bkfc-field"
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoFocus
                    />
                    <input
                      className="bkfc-field"
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    <CountrySelect value={country} onChange={setCountry} className="bkfc-field" />
                    <button className="bkfc-cta bkfc-submit" type="submit" disabled={busy || auNotReady}>
                      {busy ? 'Saving…' : 'Complete registration'}
                    </button>
                  </form>
                )}
                {error && <div className="bkfc-error">{error}</div>}
                <div className="bkfc-regnote">
                  If the group is already full, you'll be first in line for the next cohort — we'll tell you straight away either way.
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="bkfc-footer">
        <div className="bkfc-wrap">
          The BKOS Founding Cohort — built with the Pure Bookkeeping System inside.
          <br />
          Questions before you decide? Just reply to whichever email brought you here.
        </div>
      </div>
    </div>
    </TakerShell>
  );
}

// LOCKED BKOS brand navy/orange (bkos brand row is SSoT; recolored from the
// Founding Cohort greens to match CC v2.5): orange #F24E1E (CTA/brand mark/eyebrow/
// ticks/regcard border), navy #141c2a (text/headings/dark panel bg), CTA hover
// #D8400F, on-dark orange #FF7262, peach tints #FFEDE8/#FFF5F1, border #FFD9CC.
const css = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
.bkfc{font-family:'Poppins',sans-serif;color:#141c2a;background:#FFFFFF;line-height:1.65}
.bkfc *{margin:0;padding:0;box-sizing:border-box}
.bkfc-wrap{max-width:800px;margin:0 auto;padding:0 24px}
.bkfc-hero{background:linear-gradient(180deg,#FFEDE8 0%,#FFFFFF 100%);padding:56px 0 48px;text-align:center}
.bkfc-brandline{font-size:13px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:#F24E1E;margin-bottom:8px}
.bkfc-eyebrow{display:inline-block;background:#FFEDE8;color:#F24E1E;font-size:13px;font-weight:600;padding:6px 16px;border-radius:999px;margin-bottom:20px;border:1px solid #FFD9CC}
.bkfc h1{font-size:clamp(30px,5vw,44px);font-weight:800;line-height:1.15;margin-bottom:16px;color:#141c2a}
.bkfc-sub{font-size:18px;font-weight:300;color:#4b5563;max-width:620px;margin:0 auto 28px}
.bkfc-cta{display:inline-block;background:#F24E1E;color:#fff;font-size:17px;font-weight:600;padding:16px 40px;border-radius:8px;text-decoration:none;border:none;cursor:pointer}
.bkfc-cta:hover{background:#D8400F}
.bkfc-kickoff{margin-top:18px;font-size:14px;font-weight:600;color:#F24E1E}
.bkfc-kickoff-alt{margin-top:6px;font-size:13px;font-weight:400;color:#4b5563}
.bkfc section{padding:48px 0;display:block}
.bkfc-why{background:#FFF5F1;border:1px solid #FFD9CC;border-radius:20px;padding:36px 32px}
.bkfc-why h2{margin-bottom:14px}
.bkfc-pill-row{display:flex;gap:12px;flex-wrap:wrap;margin-top:18px}
.bkfc-pill{background:#fff;border:1px solid #FFD9CC;border-radius:999px;padding:8px 16px;font-size:13px;font-weight:600;color:#F24E1E}
.bkfc-lisa{border-left:4px solid #F24E1E;background:#FFF5F1;border-radius:0 12px 12px 0;padding:24px 28px}
.bkfc-lisa p{font-size:16px;color:#141c2a;margin-bottom:12px}
.bkfc-lisa-sig{font-size:14px;font-weight:600;color:#F24E1E}
.bkfc h2{font-size:26px;font-weight:700;margin-bottom:16px;color:#141c2a}
.bkfc-body{font-size:16px;color:#4b5563;margin-bottom:14px}
.bkfc-story{border-left:4px solid #FF7262;background:#FFF5F1;border-radius:0 12px 12px 0;padding:24px 28px;margin:24px 0}
.bkfc-story p{font-size:16px;color:#141c2a;margin-bottom:10px}
.bkfc-story p:last-child{margin-bottom:0}
.bkfc-agenda{list-style:none;margin:20px 0}
.bkfc-agenda li{display:flex;gap:14px;padding:14px 0;border-bottom:1px solid #FFEDE8;font-size:16px}
.bkfc-agenda li:last-child{border-bottom:none}
.bkfc-tick{flex:none;width:26px;height:26px;border-radius:50%;background:#FFEDE8;color:#F24E1E;font-weight:700;font-size:14px;display:flex;align-items:center;justify-content:center;margin-top:2px}
.bkfc-founding{background:#141c2a;color:#fff;border-radius:20px;padding:36px 32px;margin:8px 0 16px}
.bkfc-founding h3{font-size:21px;font-weight:700;margin-bottom:10px;color:#FF7262}
.bkfc-founding p{font-size:15px;color:#cdd3dd;margin-bottom:10px}
.bkfc-founding p:last-child{margin-bottom:0}
.bkfc-regcard{background:#FFFFFF;border:2px solid #F24E1E;border-radius:20px;padding:36px 32px;text-align:center;box-shadow:0 8px 28px rgba(20,28,42,.12)}
.bkfc-regcard h2{margin-bottom:6px}
.bkfc-regcard p{font-size:15px;color:#4b5563;margin-bottom:22px}
.bkfc-field{width:100%;max-width:380px;margin:0 auto 12px;display:block;font-family:'Poppins',sans-serif;font-size:15px;padding:13px 16px;border:1px solid #FFD9CC;border-radius:8px;background:#fff;color:#141c2a}
.bkfc-submit{margin-top:8px}
.bkfc-submit:disabled{opacity:.6;cursor:wait}
.bkfc-error{margin-top:14px;font-size:13px;color:#B91C1C;background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;padding:10px 14px;display:inline-block}
.bkfc-regnote{font-size:12px;color:#8b8f99;margin-top:14px}
.bkfc-spots{display:inline-block;background:#FFEDE8;color:#F24E1E;font-size:13px;font-weight:600;padding:6px 16px;border-radius:999px;margin-bottom:16px;border:1px solid #FFD9CC}
.bkfc-spots strong{font-weight:800}
.bkfc-spots-full{background:#FEF3C7;color:#92400E;border-color:#FDE68A;font-weight:600}
.bkfc-footer{padding:36px 0 48px;text-align:center;font-size:13px;color:#8b8f99;border-top:1px solid #FFEDE8;margin-top:24px}
`;
